import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Building2,
  Clock,
  Globe,
  CreditCard,
  Package2,
  DollarSign,
  Calendar,
  Smartphone,
  MapPin,
  Users,

  Bell,
  CreditCard as PaymentIcon,
  Settings as SettingsIcon,
  Sliders,
  FileText
} from "lucide-react";
import { useApplicationSettingsContext } from "../contexts/ApplicationSettingsContext";
import Business from "./settings/Business";
import FlatRates from "./settings/FlatRates";
import { Integrations } from "./settings/Integrations";
import Materials from "./settings/Materials";

import CoreOperatingCosts from "./settings/CoreOperatingCosts";
import MobileSettings from "./settings/MobileSettings";

import PaymentSettings from "./settings/PaymentSettings";
import ApplicationSettings from "./settings/ApplicationSettings";
import ResourcePermissions from "./settings/ResourcePermissions";

import Subscription from "./settings/Subscription";
import Notifications from "./settings/Notifications";

import PermissionGuard from '../components/PermissionGuard';

// Organize settings in categories
const settingsCategories = [
  {
    id: "business-settings",
    label: "Business Settings",
    items: [
      { id: "business", label: "Business", icon: Building2, component: Business },
    ]
  },
  {
    id: "configure",
    label: "Configure",
    items: [
      { id: "flat-rates", label: "Flat Rates", icon: Clock, component: FlatRates },
      { id: "materials", label: "Materials", icon: Package2, component: Materials },
      { id: "operating-costs", label: "Operating Costs", icon: DollarSign, component: CoreOperatingCosts },
    ]
  },
  {
    id: "system-settings",
    label: "System Settings",
    items: [
      { id: "application-settings", label: "Application Settings", icon: Sliders, component: ApplicationSettings },
      { id: "resource-permissions", label: "Resource Permissions", icon: Users, component: ResourcePermissions },
      // { id: "notifications", label: "Push Notifications", icon: Bell, component: Notifications }, // Chrome Desktop testing - hidden for now

      { id: "mobile", label: "Service Areas", icon: MapPin, component: MobileSettings },
      // { id: "payments", label: "Payment Processing", icon: PaymentIcon, component: PaymentSettings },
      { id: "integrations", label: "Integrations", icon: Globe, component: Integrations },
      { id: "subscription", label: "Subscription", icon: CreditCard, component: Subscription },

    ]
  }
];

export default function Settings() {
  const location = useLocation();
  const { disabledPages } = useApplicationSettingsContext();
  
  // Check if we were redirected from /online-bookings
  const wasRedirectedFromOnlineBookings = location.state && 
    location.state.from === '/online-bookings';
  const [activeTab, setActiveTab] = useState(wasRedirectedFromOnlineBookings ? 'online-bookings' : 'business');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter out disabled settings pages
  const filteredSettingsCategories = settingsCategories.map(category => ({
    ...category,
    items: category.items.filter(item => !disabledPages.includes(item.id as any))
  })).filter(category => category.items.length > 0); // Remove empty categories

  // Flatten tabs for component lookup (filtered)
  const tabs = filteredSettingsCategories.flatMap(category => category.items);

  const ActiveComponent =
    tabs.find((tab) => tab.id === activeTab)?.component || Business;
  const activeTabLabel =
    tabs.find((tab) => tab.id === activeTab)?.label || "Business";

  useEffect(() => {
    // Check if we were redirected from /online-bookings
    if (location.state && location.state.from === '/online-bookings') {
      setActiveTab('online-bookings');
    }
    
    // Check if we have an activeTab state parameter (e.g., from resource permissions button)
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    
    // If the current active tab is disabled, switch to the first available tab
    if (disabledPages.includes(activeTab as any) && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
    
    // Log the current pathname and tab for debugging
    console.log('Current path:', location.pathname);
    console.log('Active tab:', activeTab);
  }, [location.state, location.pathname, location.search, activeTab, disabledPages, tabs]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <PermissionGuard permission="settings.pageAccess">
      <div className="-mt-6 -mx-4 sm:-mx-6 md:-mx-8">
        {/* Header Bar */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
            {/* Left side - Page title */}
            <h1 className="text-lg md:text-xl font-semibold tracking-tight text-white">{activeTabLabel}</h1>
            
            {/* Right side dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="inline-flex items-center px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600"
            >
              {React.createElement(
                tabs.find((tab) => tab.id === activeTab)?.icon || Building2,
                {
                  className: "h-3.5 w-3.5 md:h-4 md:w-4",
                }
              )}
              <span className="ml-1.5 md:ml-2">{activeTabLabel}</span>
              <svg
                className={`ml-1.5 md:ml-2 h-3.5 w-3.5 md:h-4 md:w-4 transition-transform duration-200 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 md:w-72 bg-gray-800 rounded-md shadow-lg border border-gray-700 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
                <div className="py-1">
                  {filteredSettingsCategories.map((category) => (
                    <React.Fragment key={category.id}>
                      {/* Category Header */}
                      <div className="px-3 md:px-4 py-1 text-xs font-semibold text-gray-300 bg-gray-700 border-t border-b border-gray-600">
                        {category.label}
                      </div>
                      
                      {/* Category Items */}
                      {category.items.map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => {
                              setActiveTab(tab.id);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full flex items-center px-3 md:px-4 py-2.5 text-xs md:text-sm transition-colors duration-200 ${
                              activeTab === tab.id
                                ? "bg-gray-600 text-white"
                                : "text-white hover:bg-gray-600"
                            }`}
                          >
                            {React.createElement(Icon, { className: "h-3.5 w-3.5 md:h-4 md:w-4 mr-2" })}
                            {tab.label}
                          </button>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="px-4 sm:px-6 md:px-8 py-6">
          <ActiveComponent />
        </div>
      </div>
    </PermissionGuard>
  );
}
